import React, { useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Divider,
  TextField,
  Alert,
  Paper,
  InputAdornment,
  Grid,
  IconButton,
  Tooltip,
  Fade,
  Chip
} from '@mui/material';
import LoadingButton from '@mui/lab/LoadingButton';
import {
  AccountCircle,
  Phone,
  Email,
  CurrencyRupee,
  Info,
  ArrowForward,
  Tag, // for ID
  Person // for name
} from '@mui/icons-material';
import ApiEndpoints from '../network/ApiEndPoints';
import { apiErrorToast } from '../utils/ToastUtil';
import { postJsonData } from '../network/ApiController';

function PaymentForm() {
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const id = queryParams.get('id');     
  const name = queryParams.get('name'); 
  
  const [formData, setFormData] = useState({
    txnAmount: '',
  });
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const navigate = useNavigate();

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const validateForm = () => {
    if (!formData.txnAmount || isNaN(formData.txnAmount)) {
      setError('Please enter a valid amount');
      return false;
    }
   
    setError('');
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    setIsLoading(true);

    postJsonData(
      ApiEndpoints.CREATE_QR_NEW,
      {
        amount: formData.txnAmount,
        uid:id
        // name: formData.customerName,
        // email: formData.customerEmail,
        // mobile: formData.customerMobile
      },
      setIsLoading,
      (res) => {
        if (res.data?.data?.data?.payment_url) {
          setFormData({
            txnAmount: '',
            customerName: '',
            customerMobile: '',
            customerEmail: ''
          });
          window.open(res.data.data.data?.payment_url, '_blank');
        } else {
          setError(res.msg );
        }
      },
      (err) => {
        apiErrorToast(err);
        setError('Failed to initiate payment. Please try again.');
      }
    );
  };

  return (
    <Fade in={true}>
      <Paper elevation={6} sx={{
        p: { xs: 2, md: 4 },
        maxWidth: 600,
        mx: 'auto',
        my: { xs: 2, md: 4 },
        borderRadius: 3,
        background: 'linear-gradient(to bottom right, #f9f9f9, #ffffff)',
        boxShadow: '0 8px 32px rgba(0,0,0,0.1)'
      }}>
        <Box textAlign="center" mb={3}>
          <Typography variant="h4" component="h1" gutterBottom sx={{
            fontWeight: 700,
            color: 'primary.main',
            letterSpacing: 0.5
          }}>
            UPI Payment Gateway
          </Typography>
          <Typography variant="subtitle1" color="text.secondary" sx={{
            fontSize: '1.1rem',
            opacity: 0.8
          }}>
            Secure and fast payment processing
          </Typography>
          
          {/* Display name and id if they exist */}
          {(name || id) && (
            <Box mt={2} display="flex" justifyContent="center" gap={2}>
              {name && (
                <Chip
                  icon={<Person />}
                  label={`Name: ${name}`}
                  variant="outlined"
                  color="primary"
                />
              )}
              {id && (
                <Chip
                  icon={<Tag />}
                  label={`ID: ${id}`}
                  variant="outlined"
                  color="secondary"
                />
              )}
            </Box>
          )}
        </Box>
        
        <Divider sx={{ 
          my: 3,
          '&::before, &::after': {
            borderColor: 'divider',
          }
        }} />
        
        {error && (
          <Alert severity="error" sx={{ 
            mb: 3,
            borderRadius: 2,
            alignItems: 'center'
          }}>
            {error}
          </Alert>
        )}

        <Box 
          component="form" 
          onSubmit={handleSubmit} 
          sx={{ mt: 2 }}
          noValidate
        >
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Transaction Amount"
                name="txnAmount"
                type="number"
                value={formData.txnAmount}
                onChange={handleChange}
                margin="normal"
                variant="outlined"
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <CurrencyRupee color="action" />
                    </InputAdornment>
                  ),
                  inputProps: {
                    min: 1,
                    step: 1
                  }
                }}
                sx={{ 
                  mb: 2,
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                  }
                }}
              />
            </Grid>
          </Grid>

          <LoadingButton
            type="submit"
            variant="contained"
            loading={isLoading}
            fullWidth
            size="large"
            endIcon={<ArrowForward />}
            sx={{ 
              py: 1.5,
              borderRadius: 2,
              fontSize: '1rem',
              fontWeight: 600,
              textTransform: 'none',
              boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
              '&:hover': {
                boxShadow: '0 6px 16px rgba(0,0,0,0.15)',
                transform: 'translateY(-1px)'
              },
              transition: 'all 0.3s ease'
            }}
          >
            {isLoading ? 'Processing...' : 'Proceed to Payment'}
          </LoadingButton>
        </Box>
      </Paper>
    </Fade>
  );
}

export default PaymentForm;